import React, {
    RefObject,
    useCallback,
    useEffect, useImperativeHandle,
    useState
} from "react";
import Loader from "./Loader";
import {useHistory, useLocation} from "react-router-dom";
import {useDebounce} from "../../api/hooks";

interface BYTableRef {
    clearSelection: () => void
}

interface BYTableProps<T, K extends keyof T> {
    data: T[];
    columns: K[];
    columnNames: string[];
    dataTransform?: {
        [key in K]?: (arg0: T, index?: number) => string | Date | JSX.Element;
    },
    sortDataTransform?: {
        [key in K]?: (arg0: T) => string | number | Date;
    },
    onClick?: (arg0: T) => void;
    rowClassName?: (arg0: T) => string;
    keyExtractor?: (arg0: T) => string;
    onSortEquality?: (arg0: T, arg1: T) => number;
    onRowSelect?: (arg0: T[]) => void;
    loading?: boolean;
    height?: 40 | 60 | 80 | 100;
    withSelect?: boolean;
    rowsPerPage?: number;
    className?: string;
    containerClassName?: string;
    rightCustomView?: JSX.Element;
    leftCustomView?: JSX.Element;
    theadContent?: JSX.Element;
    tbodyContent?: JSX.Element;
    withPaging?: boolean;
    mRef?: RefObject<BYTableRef>
}

export const BYTable = <T extends unknown, K extends keyof T>(props: BYTableProps<T, K>) => {
    const {
        data = [],
        columns,
        columnNames,
        dataTransform,
        onClick,
        loading,
        // height = 80,
        withSelect = false,
        rowsPerPage = 50,
        rowClassName,
        className = '',
        containerClassName,
        keyExtractor,
        onSortEquality,
        sortDataTransform,
        leftCustomView,
        rightCustomView,
        theadContent,
        tbodyContent,
        onRowSelect,
        withPaging = true,
        mRef
    } = props;

    const history = useHistory();
    const location = useLocation();

    const [sortBy, setSortBy] = useState<K>(columns[0]);
    const [sortByOrder, setSortByOrder] = useState(false);
    const [sortedData, setSortedData] = useState(data);
    const [selectedData, setSelectedData] = useState<T[]>([]);
    const [currentPage, setCurrentPage] = useState(0);

    const debouncedLoading = useDebounce(loading, 300);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paramSortBy = params.get('s') as K;
        const paramSortByOrder = Boolean(params.get('o'));
        const paramCurrentPage = Number(params.get('p'));
        if (columns.includes(paramSortBy) && paramSortBy !== sortBy) {
            setSortBy(paramSortBy);
        }
        setSortByOrder(paramSortByOrder);
        setCurrentPage(paramCurrentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    useEffect(() => {
        const queryParams = {
            ...(sortBy as string !== columns[0] && {s: sortBy as string}),
            ...(sortByOrder && {o: '1'}),
            ...(currentPage && {p: currentPage.toString()}),
        } as Record<string, string>;
        const urlParams = new URLSearchParams(queryParams).toString();

        if (urlParams) {
            history.push(`${location.pathname}?${urlParams}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortByOrder, currentPage])

    useEffect(() => {
        const newSortedData = [...data].sort((a, b) => {
            let currentDataTransform = (data) => data[sortBy];

            if (sortDataTransform?.[sortBy]) {
                currentDataTransform = sortDataTransform[sortBy];
            } else if (dataTransform?.[sortBy]) {
                currentDataTransform = dataTransform[sortBy];
            }

            let aValue = currentDataTransform(a);
            let bValue = currentDataTransform(b);

            if (React.isValidElement(aValue)) {
                aValue = a[sortBy];
                bValue = b[sortBy];
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (sortByOrder) {
                    return bValue?.localeCompare(aValue) || onSortEquality?.(a, b);
                } else {
                    return aValue?.localeCompare(bValue) || onSortEquality?.(a, b);
                }
            }

            if (sortByOrder) {
                return Number(bValue) - Number(aValue) || onSortEquality?.(a, b);
            } else {
                return Number(aValue) - Number(bValue) || onSortEquality?.(a, b);
            }
        });

        setSortedData(newSortedData);

        if (newSortedData.length / rowsPerPage < currentPage) {
            setCurrentPage(0);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortByOrder, data, setSortedData, setCurrentPage, currentPage]);

    const clearSelection = useCallback(() => {
        setSelectedData([]);
        if (withSelect && onRowSelect) {
            onRowSelect([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSelectedData, onRowSelect]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => clearSelection(), [data]);

    useImperativeHandle(mRef, () => ({
        clearSelection
    }));

    const pageData = sortedData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const colSpan = columns.length + (withSelect ? 1 : 0);

    const Row = useCallback(({rowData, index}) => {
        let className;
        if (rowClassName) {
            className = rowClassName(rowData);
        }

        let key = `bytable-${index}`;
        if (keyExtractor) {
            key = keyExtractor(rowData);
        }

        return <tr className={className}>
            {withSelect && <td>
                <div className="form-check">
                    <input type="checkbox"
                           className="form-check-input"
                           checked={selectedData.includes(rowData)}
                           onChange={(e) => {
                               const {checked} = e.target;

                               const newSelectedData = checked ? [...selectedData, rowData] : selectedData.filter(e => rowData !== e);
                               setSelectedData(newSelectedData);

                               if (withSelect && onRowSelect) {
                                   onRowSelect(newSelectedData);
                               }
                           }}
                    />
                </div>
            </td>}

            {columns.map((column, indexC) => {
                let currentDataTransform = (data) => data[column];

                if (dataTransform?.[column]) {
                    currentDataTransform = dataTransform[column];
                }

                let keyC = `${key}-t-${index}-${indexC}`;

                return <td key={keyC} onClick={() => {
                    if (onClick) {
                        onClick(rowData);
                    }
                }}>
                    {currentDataTransform(rowData)}
                </td>;
            })}
        </tr>;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, withSelect, dataTransform, onClick, selectedData, rowClassName, keyExtractor]);

    return <div className={containerClassName}>

        <div className="row paging-container">
            <div className="col-3">
                {leftCustomView}
            </div>
            <div className="col-6">
                {withPaging &&
                    <div className="paging-buttons-container">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage(currentPage - 1)}
                        ><i className="fas fa-arrow-left"/>
                        </button>

                        {totalPages > 0 && <div className="pages">
                            {currentPage > 2 && <button type="button"
                                                        className={`btn btn-light btn-sm`}
                                                        onClick={() => setCurrentPage(0)}>{1}</button>
                            }

                            {Array.from(Array(totalPages > 2 ? 3 : totalPages).keys()).map((index) => {
                                let firstIndex;

                                if (currentPage === totalPages - 1 && currentPage > 1) {
                                    firstIndex = currentPage - 2;
                                } else if (currentPage > 0) {
                                    firstIndex = currentPage - 1;
                                } else {
                                    firstIndex = 0;
                                }


                                return <button type="button"
                                               key={`paging-t-${index}`}
                                               className={`btn btn-sm btn-${currentPage === (index + firstIndex) ? 'primary' : 'light'}`}
                                               disabled={currentPage === (index + firstIndex)}
                                               onClick={() => setCurrentPage(index + firstIndex)}>{firstIndex + index + 1}</button>;
                            })}

                            <span className="mx-2">de</span>
                            <button type="button"
                                    className={`btn btn-sm btn-${currentPage === (totalPages - 1) ? 'primary' : 'light'}`}
                                    disabled={currentPage === (totalPages - 1)}
                                    onClick={() => setCurrentPage(totalPages - 1)}>{totalPages}</button>
                        </div>}

                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={currentPage === totalPages - 1}
                            onClick={() => setCurrentPage(currentPage + 1)}
                        ><i className="fas fa-arrow-right"/>
                        </button>
                    </div>}
            </div>
            <div className="col-3 text-right">
                {rightCustomView}
            </div>
        </div>


        {/*<div className={`table-responsive /!*table-height-${height}*!/ mb-2`}>*/}
        <div className={`by-table-container mb-2 overflow-y`}>

            <table className={`table table-by table-sm ${className}`}>
                <thead className="table-dark with-header">
                {theadContent}
                <tr>
                    {/*{withSelect && <th/>}*/}
                    {withSelect && data.length > 0 && <th>
                        <div className="form-check">
                            <input type="checkbox"
                                   className="form-check-input"
                                   checked={selectedData.length === data.length}
                                   onChange={(e) => {
                                       const {checked} = e.target;

                                       let newSelectedData;
                                       if (selectedData && checked) {
                                           newSelectedData = [...data];
                                       } else {
                                           newSelectedData = [];
                                       }

                                       setSelectedData(newSelectedData);

                                       if (withSelect && onRowSelect) {
                                           onRowSelect(newSelectedData);
                                       }
                                   }}
                            />
                        </div>
                    </th>}


                    {columns.map((column, index) => {
                        const isColumnSortBy = column === sortBy;
                        const arrowDirection = isColumnSortBy ? (sortByOrder ? 'up' : 'down') : 'down';

                        return <th key={`header-c-${index}`}>
                            <button
                                className={`btn btn-sm btn-${isColumnSortBy ? 'primary' : 'primary-outline text-white'}`}
                                type="button"
                                key={`header-c-${index}`}
                                onClick={() => {
                                    if (isColumnSortBy) {
                                        setSortByOrder(!sortByOrder);
                                    } else {
                                        setSortByOrder(false);
                                        setSortBy(column);
                                    }
                                }}
                                disabled={columnNames[index] === 'Phase' && className==='center-content statistics-container'
                                 ? true : false}>
                                {columnNames[index]}
                                {isColumnSortBy && <>&nbsp;&nbsp; <i className={`fa fa-arrow-${arrowDirection}`}/></>}
                            </button>
                        </th>
                    })}
                </tr>
                </thead>
                <tbody className="table-hover">

                {debouncedLoading && <tr>
                    <td colSpan={colSpan} rowSpan={rowsPerPage} className="text-center"><Loader className="my-15"/></td>
                </tr>}

                {!debouncedLoading && pageData.map((data, index) => {
                    let key = `bytable-${index}`;
                    if (keyExtractor) {
                        key = keyExtractor(data);
                    }

                    return <Row key={key} rowData={data} index={index}/>;
                })}

                {!debouncedLoading && !pageData.length && <tr>
                    <td colSpan={colSpan} className="text-center">
                        <div className="p-3 pb-2"><i className="far fa-sticky-note fa-3x color-green"/></div>
                        <p>Il n'y a pas de résultats</p>
                    </td>
                </tr>}

                </tbody>
                <tfoot>
                {tbodyContent}
                {!tbodyContent && <tr className="table-dark">
                    <th colSpan={colSpan}>&nbsp;</th>
                </tr>}
                </tfoot>
            </table>
        </div>
    </div>
}
